import { useMemo } from 'react';

/**
 * Avatar — reusable user avatar component.
 *
 * Priority (best → worst fallback):
 *   1. avatarPath  → custom uploaded avatar served via /api/files/<path>?auth=<jwt>
 *   2. avatarUrl   → URL from OAuth provider (Discord/Google/Spotify)
 *   3. initials    → first letter of username / name / email
 *
 * Uses GroovLens brand teal-derived palette for fallback backgrounds, derived
 * deterministically from the user's id/email so the same person always lands
 * on the same color.
 */
const PALETTE = [
  { bg: '#18C9B9', fg: '#0B0F14' },  // teal
  { bg: '#9DDF6E', fg: '#0B0F14' },  // lime
  { bg: '#F2A65A', fg: '#0B0F14' },  // amber
  { bg: '#F26F6F', fg: '#0B0F14' },  // coral
  { bg: '#B8A0FF', fg: '#0B0F14' },  // violet
  { bg: '#6FB7F2', fg: '#0B0F14' },  // sky
];

function hashSeed(seed) {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

function extractInitial(user) {
  const candidate =
    user?.username?.trim() ||
    user?.name?.trim() ||
    user?.email?.trim() ||
    '?';
  return candidate.charAt(0).toUpperCase();
}

export function Avatar({ user, size = 40, className = '', dataTestId = 'user-avatar' }) {
  const backend = process.env.REACT_APP_BACKEND_URL;
  const token = typeof window !== 'undefined' ? localStorage.getItem('groovlens_token') : null;
  const customSrc = useMemo(() => {
    if (!user?.avatar_path) return null;
    return `${backend}/api/files/${user.avatar_path}${token ? `?auth=${encodeURIComponent(token)}` : ''}`;
  }, [backend, token, user?.avatar_path]);
  const oauthSrc = user?.avatar_url || null;
  const src = customSrc || oauthSrc;

  const initial = extractInitial(user);
  const seedSrc = user?.id || user?.email || initial;
  const color = PALETTE[hashSeed(seedSrc) % PALETTE.length];

  const style = {
    width: size,
    height: size,
    background: color.bg,
    color: color.fg,
    fontSize: Math.max(11, Math.round(size * 0.42)),
  };

  if (src) {
    return (
      <img
        src={src}
        alt={user?.username || user?.name || user?.email || 'Avatar'}
        data-testid={dataTestId}
        className={`rounded-full object-cover ring-1 ring-white/10 ${className}`}
        style={{ width: size, height: size }}
        onError={(e) => {
          // If image load fails (broken auth/expired token), strip src so the
          // fallback initial is shown instead.
          e.currentTarget.style.display = 'none';
        }}
      />
    );
  }

  return (
    <div
      data-testid={dataTestId}
      className={`rounded-full flex items-center justify-center font-display font-semibold ring-1 ring-white/10 ${className}`}
      style={style}
      aria-label={user?.username || user?.name || user?.email || 'User avatar'}
    >
      {initial}
    </div>
  );
}

export default Avatar;
